import { createApiService, type ApiError } from './api';
import type {
  LearningPlan,
  CreateLearningPlanRequest,
  UpdateLearningPlanRequest,
  Nudge,
  NudgeRequest,
  Tip,
  TipRequest,
  Tidbit,
  TidbitRequest,
  Hack,
  HackRequest,
  Lesson,
  LessonRequest,
  Nano,
  NanoRequest,
  Assessment,
  AssessmentRequest,
  Reflection,
  ReflectionRequest,
  SearchRequest,
  ActivePlanDashboardResponse,
  UserLessonResponse,
} from '@/types/learningManagement';
import type { PaginatedResponse } from '@/types/common';
import { unwrapPaginatedResponse } from '@/utils/pagination';
import { normalizeLearningPathResponse } from '@/utils/learningPathNormalizer';

// Learning & Goal Management Service API Base URL
// Uses nginx reverse proxy path for all environments (K8s, Docker, local via Vite)
const LEARNING_MGMT_API_BASE_URL =
  import.meta.env.VITE_LEARNING_MGMT_API_BASE_URL || '/api/learning-goal';

const learningMgmtApiService = createApiService(LEARNING_MGMT_API_BASE_URL);

// ============================================================================
// LearningPlan APIs
// ============================================================================

export const learningPlanApi = {
  // Create learning plan
  create: async (data: CreateLearningPlanRequest): Promise<LearningPlan> => {
    return learningMgmtApiService.post<LearningPlan>('/api/v1/learning-plans', data);
  },

  // Get learning plan by ID
  getById: async (id: string): Promise<LearningPlan> => {
    return learningMgmtApiService.get<LearningPlan>(`/api/v1/learning-plans/${id}`);
  },

  // Update learning plan
  update: async (id: string, data: UpdateLearningPlanRequest): Promise<LearningPlan> => {
    return learningMgmtApiService.put<LearningPlan>(`/api/v1/learning-plans/${id}`, data);
  },

  // Delete learning plan (soft delete)
  delete: async (id: string): Promise<void> => {
    return learningMgmtApiService.delete<void>(`/api/v1/learning-plans/${id}`);
  },

  // List learning plans with pagination
  list: async (params?: {
    page?: number;
    size?: number;
    sortBy?: string;
    direction?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<LearningPlan>> => {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.direction) queryParams.append('direction', params.direction);

    const query = queryParams.toString();
    try {
      const response = await learningMgmtApiService.get<unknown>(
        `/api/v1/learning-plans${query ? `?${query}` : ''}`
      );
      return unwrapPaginatedResponse<LearningPlan>(response);
    } catch (error: unknown) {
      console.error('[learningPlanApi.list] Error fetching learning plans:', error);
      throw error;
    }
  },

  // Search learning plans
  search: async (request: SearchRequest & { title?: string }): Promise<PaginatedResponse<LearningPlan>> => {
    const queryParams = new URLSearchParams();
    if (request.title) queryParams.append('title', request.title);
    if (request.page !== undefined) queryParams.append('page', request.page.toString());
    if (request.size !== undefined) queryParams.append('size', request.size.toString());

    const query = queryParams.toString();
    try {
      const response = await learningMgmtApiService.get<unknown>(
        `/api/v1/learning-plans/search${query ? `?${query}` : ''}`
      );
      return unwrapPaginatedResponse<LearningPlan>(response);
    } catch (error: unknown) {
      console.error('[learningPlanApi.search] Error searching learning plans:', error);
      throw error;
    }
  },

  // List learning plans by status
  listByStatus: async (
    status: string,
    params?: { page?: number; size?: number }
  ): Promise<PaginatedResponse<LearningPlan>> => {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());

    const query = queryParams.toString();
    try {
      const response = await learningMgmtApiService.get<unknown>(
        `/api/v1/learning-plans/status/${status}${query ? `?${query}` : ''}`
      );
      return unwrapPaginatedResponse<LearningPlan>(response);
    } catch (error: unknown) {
      console.error('[learningPlanApi.listByStatus] Error fetching learning plans:', error);
      throw error;
    }
  },

  // Get learning path by user email/ID
  getByUser: async (userIdentifier: string): Promise<LearningPlan> => {
    const raw = await learningMgmtApiService.get<unknown>(`/api/v1/learning-paths/users/${encodeURIComponent(userIdentifier)}`);
    if (import.meta.env.DEV && raw != null) {
      const dayLabels: { day: number; title?: string; description?: string; keys: string[] }[] = [];
      const labelKeys = ['title', 'description', 'goal_title', 'goal_description', 'goalTitle', 'goalDescription', 'goal', 'focus', 'name', 'lesson_title', 'lessonTitle'];
      const collectDaysFromModules = (modArr: unknown[]): void => {
        modArr.forEach((m: unknown) => {
          const contents = (m as Record<string, unknown>)?.dailyContents ?? (m as Record<string, unknown>)?.daily_contents ?? (m as Record<string, unknown>)?.dailyContent ?? (m as Record<string, unknown>)?.goals;
          const list = Array.isArray(contents) ? contents : [];
          list.forEach((day: unknown) => {
            const r = day as Record<string, unknown>;
            const keys = labelKeys.filter((k) => r[k] && typeof r[k] === 'string');
            dayLabels.push({
              day: dayLabels.length + 1,
              title: typeof r.title === 'string' ? r.title : undefined,
              description: typeof r.description === 'string' ? r.description : undefined,
              keys,
            });
          });
        });
      };
      if (Array.isArray(raw)) {
        const first = raw[0] as Record<string, unknown>;
        const details = first?.learningPathDetails ?? first?.learning_path_details;
        const detailsArr = Array.isArray(details) ? details : [];
        detailsArr.forEach((d: unknown) => {
          const plan = (d as Record<string, unknown>)?.userLearningPlan ?? (d as Record<string, unknown>)?.user_learning_plan;
          const mods = (plan as Record<string, unknown>)?.learningModules ?? (plan as Record<string, unknown>)?.learning_modules ?? (plan as Record<string, unknown>)?.modules;
          collectDaysFromModules(Array.isArray(mods) ? mods : []);
        });
      } else {
        const plan = raw as Record<string, unknown>;
        const mods = plan?.learningModules ?? plan?.learning_modules ?? plan?.modules;
        collectDaysFromModules(Array.isArray(mods) ? mods : []);
      }
      console.log('[Learning Path API] raw response for', userIdentifier, {
        dailyContentsCount: dayLabels.length,
        perDay: dayLabels.map((d) => `Day ${d.day}: ${d.keys.length ? (d.title ?? d.description ?? d.keys.join(',')) : '(no label)'}`),
        hint: dayLabels.length >= 7 && dayLabels.some((d, i) => i >= 3 && d.keys.length === 0)
          ? 'Days 4–7 have no title/description from backend. Backend must set title (or goal_title, etc.) for all 7 days.'
          : undefined,
      });
    }
    return normalizeLearningPathResponse(raw);
  },
};

// ============================================================================
// User Learning Plan APIs (dashboard, status management)
// ============================================================================

/** Normalize dashboard API response: unwrap data/result and map snake_case to camelCase so overdue/completed days show on learning-plan (backend may send overdue_day_numbers etc.). */
function normalizeDashboardResponse(raw: unknown): ActivePlanDashboardResponse {
  if (raw == null || typeof raw !== 'object') {
    return {};
  }
  const obj = raw as Record<string, unknown>;
  const data = (obj.data ?? obj.dashboard ?? obj.result ?? obj) as Record<string, unknown>;
  const arr = (x: unknown): number[] => {
    if (!Array.isArray(x)) return [];
    return x
      .map((n) => {
        if (typeof n === 'number') return n;
        if (typeof n === 'string') return Number(n);
        return NaN;
      })
      .filter((n): n is number => Number.isFinite(n));
  };
  return {
    ...data,
    activePlan: (data.activePlan ?? data.active_plan) as ActivePlanDashboardResponse['activePlan'],
    learningPath: (data.learningPath ?? data.learning_path) as ActivePlanDashboardResponse['learningPath'],
    currentDayNumber:
      typeof data.currentDayNumber === 'number'
        ? data.currentDayNumber
        : typeof data.current_day_number === 'number'
          ? data.current_day_number
          : undefined,
    currentDate: (data.currentDate ?? data.current_date) as string | undefined,
    todaysContent: (data.todaysContent ?? data.todays_content) as ActivePlanDashboardResponse['todaysContent'],
    todaysLessons: Array.isArray(data.todaysLessons)
      ? data.todaysLessons
      : Array.isArray(data.todays_lessons)
        ? data.todays_lessons
        : [],
    overallProgress:
      typeof data.overallProgress === 'number'
        ? data.overallProgress
        : typeof data.overall_progress === 'number'
          ? data.overall_progress
          : undefined,
    totalDays:
      typeof data.totalDays === 'number' ? data.totalDays : typeof data.total_days === 'number' ? data.total_days : undefined,
    daysCompleted:
      typeof data.daysCompleted === 'number'
        ? data.daysCompleted
        : typeof data.days_completed === 'number'
          ? data.days_completed
          : undefined,
    daysRemaining:
      typeof data.daysRemaining === 'number'
        ? data.daysRemaining
        : typeof data.days_remaining === 'number'
          ? data.days_remaining
          : undefined,
    completedDayNumbers: arr(data.completedDayNumbers ?? data.completed_day_numbers),
    overdueDayNumbers: arr(data.overdueDayNumbers ?? data.overdue_day_numbers),
    followUpPlanStatus: (data.followUpPlanStatus ?? data.follow_up_plan_status) as
      | 'NONE'
      | 'GENERATING'
      | 'READY'
      | undefined,
  } as ActivePlanDashboardResponse;
}

export const userLearningPlanApi = {
  /** Get active plan dashboard (single call for main view). Response is normalized so snake_case (e.g. overdue_day_numbers) from backend is mapped to camelCase for learning-plan overdue UI. */
  getDashboard: async (userId: string): Promise<ActivePlanDashboardResponse> => {
    const raw = await learningMgmtApiService.get<unknown>(
      `/api/v1/user-learning-plans/users/${encodeURIComponent(userId)}/dashboard`
    );
    return normalizeDashboardResponse(raw);
  },

  /** Normalize a single win from API (id, name, description, summary or snake_case). */
  normalizeWin(r: Record<string, unknown>): { id?: string; name: string; description?: string } | null {
    const name = (r.name ?? r.winDescription ?? r.win_description ?? r.description ?? '') as string;
    if (!name || typeof name !== 'string') return null;
    const description = (r.description ?? r.summary ?? r.winDescription ?? r.win_description ?? '') as string | undefined;
    const id = (r.id ?? r.winId ?? r.win_id) as string | undefined;
    return {
      ...(id && typeof id === 'string' ? { id } : {}),
      name: name.trim(),
      ...(typeof description === 'string' && description.trim() ? { description: description.trim() } : {}),
    };
  },

  /**
   * Get all active wins for the user (GET .../users/{userId}/wins).
   * Returns [] when no wins; uses skipRetry so 500/404 fail once.
   */
  getWinsForUser: async (userId: string): Promise<{ id?: string; name: string; description?: string }[]> => {
    try {
      const res = await learningMgmtApiService.get<unknown>(
        `/api/v1/user-learning-plans/users/${encodeURIComponent(userId)}/wins`,
        { skipRetry: true }
      );
      if (!Array.isArray(res)) return [];
      const out: { id?: string; name: string; description?: string }[] = [];
      for (const item of res) {
        if (item && typeof item === 'object') {
          const w = userLearningPlanApi.normalizeWin(item as Record<string, unknown>);
          if (w) out.push(w);
        }
      }
      return out;
    } catch {
      return [];
    }
  },

  /**
   * Get the primary win for the user (first of active wins). Uses getWinsForUser, returns wins[0] or null.
   */
  getWinForUser: async (userId: string): Promise<{ name: string; description?: string } | null> => {
    const wins = await userLearningPlanApi.getWinsForUser(userId);
    const first = wins[0];
    return first ? { name: first.name, description: first.description } : null;
  },

  /** Get all active (IN_PROGRESS) plans for user */
  getActive: async (userId: string): Promise<unknown> => {
    return learningMgmtApiService.get(`/api/v1/user-learning-plans/users/${encodeURIComponent(userId)}/active`);
  },

  /** Start a plan */
  start: async (planId: string): Promise<unknown> => {
    return learningMgmtApiService.patch(`/api/v1/user-learning-plans/${planId}/start`);
  },

  /** Complete a plan (triggers async follow-up generation) */
  complete: async (planId: string): Promise<unknown> => {
    return learningMgmtApiService.patch(`/api/v1/user-learning-plans/${planId}/complete`);
  },

  /** Pause an active plan */
  pause: async (planId: string): Promise<unknown> => {
    return learningMgmtApiService.patch(`/api/v1/user-learning-plans/${planId}/pause`);
  },

  /** Resume a paused plan */
  resume: async (planId: string): Promise<unknown> => {
    return learningMgmtApiService.patch(`/api/v1/user-learning-plans/${planId}/resume`);
  },
};

// ============================================================================
// User Lesson APIs (retrieve, status actions)
// ============================================================================

export const userLessonApi = {
  /** Get single lesson detail */
  getById: async (userLessonId: string): Promise<UserLessonResponse> => {
    return learningMgmtApiService.get<UserLessonResponse>(`/api/v1/user-lessons/${userLessonId}`);
  },

  /** Get all lessons for user */
  getByUser: async (userId: string): Promise<UserLessonResponse[]> => {
    const res = await learningMgmtApiService.get<unknown>(`/api/v1/user-lessons/users/${encodeURIComponent(userId)}`);
    return Array.isArray(res) ? res : [];
  },

  /** Get lessons by status */
  getByStatus: async (userId: string, status: string): Promise<UserLessonResponse[]> => {
    const res = await learningMgmtApiService.get<unknown>(
      `/api/v1/user-lessons/users/${encodeURIComponent(userId)}/status/${status}`
    );
    return Array.isArray(res) ? res : [];
  },

  /** Get today's lessons */
  getToday: async (userId: string): Promise<UserLessonResponse[]> => {
    const res = await learningMgmtApiService.get<unknown>(`/api/v1/user-lessons/users/${encodeURIComponent(userId)}/today`);
    return Array.isArray(res) ? res : [];
  },

  /** Get lessons for a plan */
  getByPlan: async (userId: string, planId: string): Promise<UserLessonResponse[]> => {
    const res = await learningMgmtApiService.get<unknown>(
      `/api/v1/user-lessons/users/${encodeURIComponent(userId)}/plans/${planId}`
    );
    return Array.isArray(res) ? res : [];
  },

  /** Get lessons for a specific day (1-based) */
  getByPlanAndDay: async (
    userId: string,
    planId: string,
    dayNumber: number
  ): Promise<UserLessonResponse[]> => {
    const res = await learningMgmtApiService.get<unknown>(
      `/api/v1/user-lessons/users/${encodeURIComponent(userId)}/plans/${planId}/days/${dayNumber}`
    );
    return Array.isArray(res) ? res : [];
  },

  /** Start lesson (DRAFT/PLANNED → ACTIVE) */
  start: async (userLessonId: string): Promise<UserLessonResponse> => {
    return learningMgmtApiService.patch<UserLessonResponse>(`/api/v1/user-lessons/${userLessonId}/start`);
  },

  /** Mark lesson as viewed */
  view: async (userLessonId: string): Promise<UserLessonResponse> => {
    return learningMgmtApiService.patch<UserLessonResponse>(`/api/v1/user-lessons/${userLessonId}/view`);
  },

  /** Complete lesson */
  complete: async (userLessonId: string): Promise<UserLessonResponse> => {
    return learningMgmtApiService.patch<UserLessonResponse>(`/api/v1/user-lessons/${userLessonId}/complete`);
  },

  /** Dismiss lesson (sets ARCHIVED) */
  dismiss: async (userLessonId: string): Promise<UserLessonResponse> => {
    return learningMgmtApiService.patch<UserLessonResponse>(`/api/v1/user-lessons/${userLessonId}/dismiss`);
  },
};

// ============================================================================
// User content APIs (fetch by ID for daily content — tips, nudges, tidbits, hacks, nanos)
// Backend may return { data: T }; unwrap so callers get the entity with content/description.
// ============================================================================

function unwrapEntity<T>(raw: T | { data?: T }): T {
  if (raw && typeof raw === 'object' && 'data' in raw && (raw as { data?: T }).data !== undefined) {
    return (raw as { data: T }).data;
  }
  return raw as T;
}

export const userTipApi = {
  getById: async (userTipId: string): Promise<Tip> => {
    const raw = await learningMgmtApiService.get<Tip | { data: Tip }>(`/api/v1/user-tips/${userTipId}`);
    return unwrapEntity(raw);
  },
};

export const userNudgeApi = {
  getById: async (userNudgeId: string): Promise<Nudge> => {
    const raw = await learningMgmtApiService.get<Nudge | { data: Nudge }>(`/api/v1/user-nudges/${userNudgeId}`);
    return unwrapEntity(raw);
  },

  /** Get personalized nudges for a user. Returns array (may be wrapped in { content } or { data }). */
  getByUser: async (userId: string): Promise<Record<string, unknown>[]> => {
    try {
      const raw = await learningMgmtApiService.get<unknown>(
        `/api/v1/nudges/personalized/${encodeURIComponent(userId)}`
      );
      // Backend may return array directly, or { content: [] }, or { data: [] }
      if (Array.isArray(raw)) return raw as Record<string, unknown>[];
      if (raw && typeof raw === 'object') {
        const obj = raw as Record<string, unknown>;
        const list = obj.content ?? obj.data ?? obj.nudges ?? obj.results;
        if (Array.isArray(list)) return list as Record<string, unknown>[];
      }
      return [];
    } catch {
      return [];
    }
  },
};

export const userTidbitApi = {
  getById: async (userTidbitId: string): Promise<Tidbit> => {
    const raw = await learningMgmtApiService.get<Tidbit | { data: Tidbit }>(`/api/v1/user-tidbits/${userTidbitId}`);
    return unwrapEntity(raw);
  },
};

export const userHackApi = {
  getById: async (userHackId: string): Promise<Hack> => {
    const raw = await learningMgmtApiService.get<Hack | { data: Hack }>(`/api/v1/user-hacks/${userHackId}`);
    return unwrapEntity(raw);
  },
};

export const userNanoApi = {
  getById: async (userNanoId: string): Promise<Nano> => {
    const raw = await learningMgmtApiService.get<Nano | { data: Nano }>(`/api/v1/user-nanos/${userNanoId}`);
    return unwrapEntity(raw);
  },
};

// ============================================================================
// Nudge APIs
// ============================================================================

export const nudgeApi = {
  // Create nudge
  create: async (data: NudgeRequest): Promise<Nudge> => {
    return learningMgmtApiService.post<Nudge>('/api/v1/nudges', data);
  },

  // Get nudge by ID
  getById: async (nudgeId: string): Promise<Nudge> => {
    return learningMgmtApiService.get<Nudge>(`/api/v1/nudges/${nudgeId}`);
  },

  // Update nudge
  update: async (nudgeId: string, data: Partial<NudgeRequest>): Promise<Nudge> => {
    return learningMgmtApiService.put<Nudge>(`/api/v1/nudges/${nudgeId}`, data);
  },

  // Delete nudge (soft delete)
  delete: async (nudgeId: string): Promise<void> => {
    return learningMgmtApiService.delete<void>(`/api/v1/nudges/${nudgeId}`);
  },

  // List nudges with pagination
  list: async (params?: {
    page?: number;
    size?: number;
    sortBy?: string;
    sortDirection?: 'ASC' | 'DESC';
  }): Promise<PaginatedResponse<Nudge>> => {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortDirection) queryParams.append('sortDirection', params.sortDirection);

    const query = queryParams.toString();
    try {
      const response = await learningMgmtApiService.get<unknown>(
        `/api/v1/nudges${query ? `?${query}` : ''}`
      );
      return unwrapPaginatedResponse<Nudge>(response);
    } catch (error: unknown) {
      console.error('[nudgeApi.list] Error fetching nudges:', error);
      throw error;
    }
  },

  // Search nudges
  search: async (request: SearchRequest & { query?: string }): Promise<PaginatedResponse<Nudge>> => {
    const queryParams = new URLSearchParams();
    if (request.query) queryParams.append('query', request.query);
    if (request.page !== undefined) queryParams.append('page', request.page.toString());
    if (request.size !== undefined) queryParams.append('size', request.size.toString());

    const query = queryParams.toString();
    try {
      const response = await learningMgmtApiService.get<unknown>(
        `/api/v1/nudges/search${query ? `?${query}` : ''}`
      );
      return unwrapPaginatedResponse<Nudge>(response);
    } catch (error: unknown) {
      console.error('[nudgeApi.search] Error searching nudges:', error);
      throw error;
    }
  },

  // List nudges by category
  listByCategory: async (
    category: string,
    params?: { page?: number; size?: number }
  ): Promise<PaginatedResponse<Nudge>> => {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());

    const query = queryParams.toString();
    try {
      const response = await learningMgmtApiService.get<unknown>(
        `/api/v1/nudges/category/${category}${query ? `?${query}` : ''}`
      );
      return unwrapPaginatedResponse<Nudge>(response);
    } catch (error: unknown) {
      console.error('[nudgeApi.listByCategory] Error fetching nudges:', error);
      throw error;
    }
  },

  // Deliver nudge to user (for ADMIN/USER roles)
  deliver: async (
    nudgeId: string,
    userId: string,
    deliveryChannel: string
  ): Promise<void> => {
    const queryParams = new URLSearchParams();
    queryParams.append('deliveryChannel', deliveryChannel);

    return learningMgmtApiService.post<void>(
      `/api/v1/nudges/${nudgeId}/deliver/${userId}?${queryParams.toString()}`,
      {}
    );
  },
};

// ============================================================================
// Tip APIs
// ============================================================================

export const tipApi = {
  // Create tip
  create: async (data: TipRequest): Promise<Tip> => {
    return learningMgmtApiService.post<Tip>('/api/v1/tips', data);
  },

  // Get tip by ID
  getById: async (tipId: string): Promise<Tip> => {
    return learningMgmtApiService.get<Tip>(`/api/v1/tips/${tipId}`);
  },

  // Update tip
  update: async (tipId: string, data: Partial<TipRequest>): Promise<Tip> => {
    return learningMgmtApiService.put<Tip>(`/api/v1/tips/${tipId}`, data);
  },

  // Delete tip (soft delete)
  delete: async (tipId: string): Promise<void> => {
    return learningMgmtApiService.delete<void>(`/api/v1/tips/${tipId}`);
  },

  // List tips with pagination
  list: async (params?: {
    page?: number;
    size?: number;
    sortBy?: string;
    sortDirection?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<Tip>> => {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortDirection) queryParams.append('sortDirection', params.sortDirection);

    const query = queryParams.toString();
    try {
      const response = await learningMgmtApiService.get<unknown>(
        `/api/v1/tips${query ? `?${query}` : ''}`
      );
      return unwrapPaginatedResponse<Tip>(response);
    } catch (error: unknown) {
      console.error('[tipApi.list] Error fetching tips:', error);
      throw error;
    }
  },

  // Search tips
  search: async (request: SearchRequest & { query?: string }): Promise<PaginatedResponse<Tip>> => {
    const queryParams = new URLSearchParams();
    if (request.query) queryParams.append('query', request.query);
    if (request.page !== undefined) queryParams.append('page', request.page.toString());
    if (request.size !== undefined) queryParams.append('size', request.size.toString());

    const query = queryParams.toString();
    try {
      const response = await learningMgmtApiService.get<unknown>(
        `/api/v1/tips/search${query ? `?${query}` : ''}`
      );
      return unwrapPaginatedResponse<Tip>(response);
    } catch (error: unknown) {
      console.error('[tipApi.search] Error searching tips:', error);
      throw error;
    }
  },

  // List tips by category
  listByCategory: async (
    category: string,
    params?: { page?: number; size?: number }
  ): Promise<PaginatedResponse<Tip>> => {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());

    const query = queryParams.toString();
    try {
      const response = await learningMgmtApiService.get<unknown>(
        `/api/v1/tips/category/${category}${query ? `?${query}` : ''}`
      );
      return unwrapPaginatedResponse<Tip>(response);
    } catch (error: unknown) {
      console.error('[tipApi.listByCategory] Error fetching tips:', error);
      throw error;
    }
  },

  // List tips by difficulty
  listByDifficulty: async (
    difficulty: string,
    params?: { page?: number; size?: number }
  ): Promise<PaginatedResponse<Tip>> => {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());

    const query = queryParams.toString();
    try {
      const response = await learningMgmtApiService.get<unknown>(
        `/api/v1/tips/difficulty/${difficulty}${query ? `?${query}` : ''}`
      );
      return unwrapPaginatedResponse<Tip>(response);
    } catch (error: unknown) {
      console.error('[tipApi.listByDifficulty] Error fetching tips:', error);
      throw error;
    }
  },
};

// ============================================================================
// Tidbit APIs
// ============================================================================

export const tidbitApi = {
  // Create tidbit
  create: async (data: TidbitRequest): Promise<Tidbit> => {
    return learningMgmtApiService.post<Tidbit>('/api/v1/tidbits', data);
  },

  // Get tidbit by ID
  getById: async (tidbitId: string): Promise<Tidbit> => {
    return learningMgmtApiService.get<Tidbit>(`/api/v1/tidbits/${tidbitId}`);
  },

  // Update tidbit
  update: async (tidbitId: string, data: Partial<TidbitRequest>): Promise<Tidbit> => {
    return learningMgmtApiService.put<Tidbit>(`/api/v1/tidbits/${tidbitId}`, data);
  },

  // Delete tidbit (soft delete)
  delete: async (tidbitId: string): Promise<void> => {
    return learningMgmtApiService.delete<void>(`/api/v1/tidbits/${tidbitId}`);
  },

  // List tidbits with pagination
  list: async (params?: {
    page?: number;
    size?: number;
    sortBy?: string;
    sortDirection?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<Tidbit>> => {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortDirection) queryParams.append('sortDirection', params.sortDirection);

    const query = queryParams.toString();
    try {
      const response = await learningMgmtApiService.get<unknown>(
        `/api/v1/tidbits${query ? `?${query}` : ''}`
      );
      return unwrapPaginatedResponse<Tidbit>(response);
    } catch (error: unknown) {
      console.error('[tidbitApi.list] Error fetching tidbits:', error);
      throw error;
    }
  },

  // Search tidbits
  search: async (request: SearchRequest & { query?: string }): Promise<PaginatedResponse<Tidbit>> => {
    const queryParams = new URLSearchParams();
    if (request.query) queryParams.append('query', request.query);
    if (request.page !== undefined) queryParams.append('page', request.page.toString());
    if (request.size !== undefined) queryParams.append('size', request.size.toString());

    const query = queryParams.toString();
    try {
      const response = await learningMgmtApiService.get<unknown>(
        `/api/v1/tidbits/search${query ? `?${query}` : ''}`
      );
      return unwrapPaginatedResponse<Tidbit>(response);
    } catch (error: unknown) {
      console.error('[tidbitApi.search] Error searching tidbits:', error);
      throw error;
    }
  },

  // List tidbits by category
  listByCategory: async (
    category: string,
    params?: { page?: number; size?: number }
  ): Promise<PaginatedResponse<Tidbit>> => {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());

    const query = queryParams.toString();
    try {
      const response = await learningMgmtApiService.get<unknown>(
        `/api/v1/tidbits/category/${category}${query ? `?${query}` : ''}`
      );
      return unwrapPaginatedResponse<Tidbit>(response);
    } catch (error: unknown) {
      console.error('[tidbitApi.listByCategory] Error fetching tidbits:', error);
      throw error;
    }
  },

  // List tidbits by interest level
  listByInterest: async (
    level: string,
    params?: { page?: number; size?: number }
  ): Promise<PaginatedResponse<Tidbit>> => {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());

    const query = queryParams.toString();
    try {
      const response = await learningMgmtApiService.get<unknown>(
        `/api/v1/tidbits/interest/${level}${query ? `?${query}` : ''}`
      );
      return unwrapPaginatedResponse<Tidbit>(response);
    } catch (error: unknown) {
      console.error('[tidbitApi.listByInterest] Error fetching tidbits:', error);
      throw error;
    }
  },
};

// ============================================================================
// Hack APIs
// ============================================================================

export const hackApi = {
  // Create hack
  create: async (data: HackRequest): Promise<Hack> => {
    return learningMgmtApiService.post<Hack>('/api/v1/hacks', data);
  },

  // Get hack by ID
  getById: async (hackId: string): Promise<Hack> => {
    return learningMgmtApiService.get<Hack>(`/api/v1/hacks/${hackId}`);
  },

  // Update hack
  update: async (hackId: string, data: Partial<HackRequest>): Promise<Hack> => {
    return learningMgmtApiService.put<Hack>(`/api/v1/hacks/${hackId}`, data);
  },

  // Delete hack (soft delete)
  delete: async (hackId: string): Promise<void> => {
    return learningMgmtApiService.delete<void>(`/api/v1/hacks/${hackId}`);
  },

  // List hacks with pagination
  list: async (params?: {
    page?: number;
    size?: number;
    sortBy?: string;
    sortDirection?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<Hack>> => {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortDirection) queryParams.append('sortDirection', params.sortDirection);

    const query = queryParams.toString();
    try {
      const response = await learningMgmtApiService.get<unknown>(
        `/api/v1/hacks${query ? `?${query}` : ''}`
      );
      return unwrapPaginatedResponse<Hack>(response);
    } catch (error: unknown) {
      console.error('[hackApi.list] Error fetching hacks:', error);
      throw error;
    }
  },

  // Search hacks
  search: async (request: SearchRequest & { query?: string }): Promise<PaginatedResponse<Hack>> => {
    const queryParams = new URLSearchParams();
    if (request.query) queryParams.append('query', request.query);
    if (request.page !== undefined) queryParams.append('page', request.page.toString());
    if (request.size !== undefined) queryParams.append('size', request.size.toString());

    const query = queryParams.toString();
    try {
      const response = await learningMgmtApiService.get<unknown>(
        `/api/v1/hacks/search${query ? `?${query}` : ''}`
      );
      return unwrapPaginatedResponse<Hack>(response);
    } catch (error: unknown) {
      console.error('[hackApi.search] Error searching hacks:', error);
      throw error;
    }
  },

  // List hacks by category
  listByCategory: async (
    category: string,
    params?: { page?: number; size?: number }
  ): Promise<PaginatedResponse<Hack>> => {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());

    const query = queryParams.toString();
    try {
      const response = await learningMgmtApiService.get<unknown>(
        `/api/v1/hacks/category/${category}${query ? `?${query}` : ''}`
      );
      return unwrapPaginatedResponse<Hack>(response);
    } catch (error: unknown) {
      console.error('[hackApi.listByCategory] Error fetching hacks:', error);
      throw error;
    }
  },

  // List hacks by complexity
  listByComplexity: async (
    level: string,
    params?: { page?: number; size?: number }
  ): Promise<PaginatedResponse<Hack>> => {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());

    const query = queryParams.toString();
    try {
      const response = await learningMgmtApiService.get<unknown>(
        `/api/v1/hacks/complexity/${level}${query ? `?${query}` : ''}`
      );
      return unwrapPaginatedResponse<Hack>(response);
    } catch (error: unknown) {
      console.error('[hackApi.listByComplexity] Error fetching hacks:', error);
      throw error;
    }
  },
};

// ============================================================================
// Lesson APIs
// ============================================================================

export const lessonApi = {
  // Create lesson
  create: async (data: LessonRequest): Promise<Lesson> => {
    return learningMgmtApiService.post<Lesson>('/api/v1/lessons', data);
  },

  // Get lesson by ID
  getById: async (lessonId: string): Promise<Lesson> => {
    return learningMgmtApiService.get<Lesson>(`/api/v1/lessons/${lessonId}`);
  },

  // Update lesson
  update: async (lessonId: string, data: Partial<LessonRequest>): Promise<Lesson> => {
    return learningMgmtApiService.put<Lesson>(`/api/v1/lessons/${lessonId}`, data);
  },

  // Delete lesson
  delete: async (lessonId: string): Promise<void> => {
    return learningMgmtApiService.delete<void>(`/api/v1/lessons/${lessonId}`);
  },

  // List lessons with pagination
  list: async (params?: {
    page?: number;
    size?: number;
    sortBy?: string;
    sortDirection?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<Lesson>> => {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortDirection) queryParams.append('sortDirection', params.sortDirection);

    const query = queryParams.toString();
    try {
      const response = await learningMgmtApiService.get<unknown>(
        `/api/v1/lessons${query ? `?${query}` : ''}`
      );
      return unwrapPaginatedResponse<Lesson>(response);
    } catch (error: unknown) {
      console.error('[lessonApi.list] Error fetching lessons:', error);
      throw error;
    }
  },

  // Search lessons
  search: async (request: SearchRequest & { query?: string }): Promise<PaginatedResponse<Lesson>> => {
    const queryParams = new URLSearchParams();
    if (request.query) queryParams.append('query', request.query);
    if (request.page !== undefined) queryParams.append('page', request.page.toString());
    if (request.size !== undefined) queryParams.append('size', request.size.toString());

    const query = queryParams.toString();
    try {
      const response = await learningMgmtApiService.get<unknown>(
        `/api/v1/lessons/search${query ? `?${query}` : ''}`
      );
      return unwrapPaginatedResponse<Lesson>(response);
    } catch (error: unknown) {
      console.error('[lessonApi.search] Error searching lessons:', error);
      throw error;
    }
  },
};

// ============================================================================
// Nano APIs
// ============================================================================

export const nanoApi = {
  // Create nano
  create: async (data: NanoRequest): Promise<Nano> => {
    return learningMgmtApiService.post<Nano>('/api/v1/nanos', data);
  },

  // Get nano by ID
  getById: async (nanoId: string): Promise<Nano> => {
    return learningMgmtApiService.get<Nano>(`/api/v1/nanos/${nanoId}`);
  },

  // Update nano
  update: async (nanoId: string, data: Partial<NanoRequest>): Promise<Nano> => {
    return learningMgmtApiService.put<Nano>(`/api/v1/nanos/${nanoId}`, data);
  },

  // Delete nano
  delete: async (nanoId: string): Promise<void> => {
    return learningMgmtApiService.delete<void>(`/api/v1/nanos/${nanoId}`);
  },

  // List nanos with pagination
  list: async (params?: {
    page?: number;
    size?: number;
    sortBy?: string;
    sortDirection?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<Nano>> => {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortDirection) queryParams.append('sortDirection', params.sortDirection);

    const query = queryParams.toString();
    try {
      const response = await learningMgmtApiService.get<unknown>(
        `/api/v1/nanos${query ? `?${query}` : ''}`
      );
      return unwrapPaginatedResponse<Nano>(response);
    } catch (error: unknown) {
      console.error('[nanoApi.list] Error fetching nanos:', error);
      throw error;
    }
  },

  // Search nanos (using list endpoint with query params)
  search: async (request: SearchRequest & { query?: string }): Promise<PaginatedResponse<Nano>> => {
    const queryParams = new URLSearchParams();
    if (request.query) queryParams.append('query', request.query);
    if (request.page !== undefined) queryParams.append('page', request.page.toString());
    if (request.size !== undefined) queryParams.append('size', request.size.toString());

    const query = queryParams.toString();
    try {
      // Use list endpoint with query params for search
      const response = await learningMgmtApiService.get<unknown>(
        `/api/v1/nanos${query ? `?${query}` : ''}`
      );
      return unwrapPaginatedResponse<Nano>(response);
    } catch (error: unknown) {
      console.error('[nanoApi.search] Error searching nanos:', error);
      throw error;
    }
  },
};

// ============================================================================
// Assessment APIs
// ============================================================================

export const assessmentApi = {
  // Create assessment
  create: async (data: AssessmentRequest): Promise<Assessment> => {
    return learningMgmtApiService.post<Assessment>('/api/v1/assessments', data);
  },

  // Get assessment by ID
  getById: async (assessmentId: string): Promise<Assessment> => {
    return learningMgmtApiService.get<Assessment>(`/api/v1/assessments/${assessmentId}`);
  },

  // Update assessment
  update: async (assessmentId: string, data: Partial<AssessmentRequest>): Promise<Assessment> => {
    return learningMgmtApiService.put<Assessment>(`/api/v1/assessments/${assessmentId}`, data);
  },

  // Delete assessment
  delete: async (assessmentId: string): Promise<void> => {
    return learningMgmtApiService.delete<void>(`/api/v1/assessments/${assessmentId}`);
  },

  // List assessments with pagination
  list: async (params?: {
    page?: number;
    size?: number;
    sortBy?: string;
    sortDirection?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<Assessment>> => {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortDirection) queryParams.append('sortDirection', params.sortDirection);

    const query = queryParams.toString();
    try {
      const response = await learningMgmtApiService.get<unknown>(
        `/api/v1/assessments${query ? `?${query}` : ''}`
      );
      return unwrapPaginatedResponse<Assessment>(response);
    } catch (error: unknown) {
      console.error('[assessmentApi.list] Error fetching assessments:', error);
      throw error;
    }
  },

  // Search assessments
  search: async (request: SearchRequest & { query?: string }): Promise<PaginatedResponse<Assessment>> => {
    const queryParams = new URLSearchParams();
    if (request.query) queryParams.append('query', request.query);
    if (request.page !== undefined) queryParams.append('page', request.page.toString());
    if (request.size !== undefined) queryParams.append('size', request.size.toString());

    const query = queryParams.toString();
    try {
      const response = await learningMgmtApiService.get<unknown>(
        `/api/v1/assessments/search${query ? `?${query}` : ''}`
      );
      return unwrapPaginatedResponse<Assessment>(response);
    } catch (error: unknown) {
      console.error('[assessmentApi.search] Error searching assessments:', error);
      throw error;
    }
  },
};

// ============================================================================
// Reflection APIs
// ============================================================================

export const reflectionApi = {
  // Create reflection
  create: async (data: ReflectionRequest): Promise<Reflection> => {
    return learningMgmtApiService.post<Reflection>('/api/v1/reflections', data);
  },

  // Get reflection by ID
  getById: async (reflectionId: string): Promise<Reflection> => {
    return learningMgmtApiService.get<Reflection>(`/api/v1/reflections/${reflectionId}`);
  },

  // Update reflection
  update: async (reflectionId: string, data: Partial<ReflectionRequest>): Promise<Reflection> => {
    return learningMgmtApiService.put<Reflection>(`/api/v1/reflections/${reflectionId}`, data);
  },

  // Delete reflection
  delete: async (reflectionId: string): Promise<void> => {
    return learningMgmtApiService.delete<void>(`/api/v1/reflections/${reflectionId}`);
  },

  // List reflections with pagination
  list: async (params?: {
    page?: number;
    size?: number;
    sortBy?: string;
    sortDirection?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<Reflection>> => {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortDirection) queryParams.append('sortDirection', params.sortDirection);

    const query = queryParams.toString();
    try {
      const response = await learningMgmtApiService.get<unknown>(
        `/api/v1/reflections${query ? `?${query}` : ''}`
      );
      return unwrapPaginatedResponse<Reflection>(response);
    } catch (error: unknown) {
      console.error('[reflectionApi.list] Error fetching reflections:', error);
      throw error;
    }
  },

  // Search reflections
  search: async (request: SearchRequest & { query?: string }): Promise<PaginatedResponse<Reflection>> => {
    const queryParams = new URLSearchParams();
    if (request.query) queryParams.append('query', request.query);
    if (request.page !== undefined) queryParams.append('page', request.page.toString());
    if (request.size !== undefined) queryParams.append('size', request.size.toString());

    const query = queryParams.toString();
    try {
      const response = await learningMgmtApiService.get<unknown>(
        `/api/v1/reflections/search${query ? `?${query}` : ''}`
      );
      return unwrapPaginatedResponse<Reflection>(response);
    } catch (error: unknown) {
      console.error('[reflectionApi.search] Error searching reflections:', error);
      throw error;
    }
  },
};

// ============================================================================
// Weekly Reflection API (learner-facing)
// ============================================================================

export interface WeeklyReflectionSubmission {
  userId: string;
  learningPathId: string;
  weekStartDate: string;
  weekEndDate: string;
  learningPathProgressRating: number;
  skillAccomplishments: string[];
  skillChallenges: string[];
  skillLearnings: string[];
  reflectionNotes?: string;
  metadata?: Record<string, unknown>;
}

export interface WeeklyReflectionResponse {
  id: string;
  userId: string;
  learningPathId: string;
  learningPathProgressRating: number;
  completedAt?: string;
}

export const weeklyReflectionApi = {
  submit: async (data: WeeklyReflectionSubmission): Promise<WeeklyReflectionResponse> => {
    return learningMgmtApiService.post<WeeklyReflectionResponse>('/api/v1/weekly-reflections', data);
  },

  getByLearningPath: async (userId: string, learningPathId: string): Promise<WeeklyReflectionResponse[]> => {
    try {
      const res = await learningMgmtApiService.get<unknown>(
        `/api/v1/weekly-reflections/users/${encodeURIComponent(userId)}/learning-paths/${learningPathId}?page=0&size=10`
      );
      const data = res && typeof res === 'object' && 'content' in (res as Record<string, unknown>)
        ? ((res as Record<string, unknown>).content as WeeklyReflectionResponse[])
        : [];
      return Array.isArray(data) ? data : [];
    } catch {
      return [];
    }
  },
};

// ============================================================================
// User Assessment API (learner-facing quiz)
// ============================================================================

export interface UserAssessmentStartResponse {
  id: string;
  userId?: string;
  assessmentGeneratedFrom?: string;
  title?: string;
  description?: string;
  questions?: AssessmentQuestionResponse[];
  totalQuestions?: number;
  timeLimitMinutes?: number;
  passingScorePercentage?: number;
  status?: string;
}

export interface AssessmentQuestionResponse {
  questionId: string;
  questionText: string;
  questionType?: string;
  points?: number;
  answerOptions?: AnswerOptionResponse[];
  explanation?: string;
}

export interface AnswerOptionResponse {
  optionId: string;
  optionText: string;
  displayOrder?: number;
  // isCorrect intentionally omitted — not sent to frontend during quiz
}

export interface UserAssessmentResultResponse {
  id: string;
  score?: number;
  percentageScore?: number;
  passed?: boolean;
  status?: string;
  completedAt?: string;
}

export const userAssessmentApi = {
  /** Start or resume an assessment for a user */
  start: async (assessmentId: string, userId: string): Promise<UserAssessmentStartResponse> => {
    return learningMgmtApiService.post<UserAssessmentStartResponse>(
      `/api/v1/assessments/${assessmentId}/start?userId=${encodeURIComponent(userId)}`,
      {}
    );
  },

  /** Submit answers for a user assessment */
  submit: async (
    userAssessmentId: string,
    answers: Array<{ questionId: string; selectedOptionId: string }>
  ): Promise<UserAssessmentResultResponse> => {
    return learningMgmtApiService.post<UserAssessmentResultResponse>(
      `/api/v1/assessments/user-assessments/${userAssessmentId}/submit`,
      { answers }
    );
  },

  /** Get a specific user assessment by ID */
  getById: async (userAssessmentId: string): Promise<UserAssessmentStartResponse> => {
    return learningMgmtApiService.get<UserAssessmentStartResponse>(
      `/api/v1/assessments/user-assessments/${userAssessmentId}`
    );
  },

  /** Get completed assessments for user */
  getCompleted: async (userId: string): Promise<UserAssessmentResultResponse[]> => {
    try {
      const res = await learningMgmtApiService.get<unknown>(
        `/api/v1/assessments/user-assessments/users/${encodeURIComponent(userId)}/completed`
      );
      return Array.isArray(res) ? res : [];
    } catch {
      return [];
    }
  },
};

export { learningMgmtApiService };
export type { ApiError };
